'use client';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';

import { APIErrorResponseGenerator } from '@/lib/helper';

import { SIZE_ENUM } from '@/components/@base/@helpers/types';
import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import Group from '@/components/@base/input/@shared/group';
import Label from '@/components/@base/input/@shared/label';
import CheckBox from '@/components/@base/input/check-box';
import { showToast } from '@/components/@base/toast/show-toast';
import { Text } from '@/components/@base/typography';
import Props from '@/components/app/product/components/affiliate-card/affiliate-rules-modal/type';
import { formatParagraphNewLine } from '@/components/app/product/helpers';
import { useGetAcceptAffiliateRules } from '@/components/app/product/services/hooks';

import useCommonModalStore from '@/store/common-modal-store';

import { pageLevelLocalization } from '@/constant/localization';

const { product: productLocalization } = pageLevelLocalization;

export default function AffiliateRulesModal({ onUpdateAffiliateUrl }: Props) {
  const { setShow } = useCommonModalStore();
  const [accepted, setAccepted] = useState(false);
  const {
    data,
    refetch: acceptAffiliateRules,
    isSuccess,
    isError,
    error,
    isLoading,
  } = useGetAcceptAffiliateRules();

  const handleAcceptAffiliateRules = () => {
    acceptAffiliateRules();
  };

  useEffect(() => {
    if (isError) {
      closeModal();
      if (error instanceof AxiosError) {
        showToast(APIErrorResponseGenerator(error));
      }
    }
    if (isSuccess) {
      closeModal();
      onUpdateAffiliateUrl(data.payload.affiliateTag);
    }
  }, [isSuccess, isError]);

  const closeModal = () => {
    setShow(false);
  };

  return (
    <Container className='max-w-[600px]'>
      <Container className='custom-scrollbar max-h-[calc(90vh-140px)] overflow-y-auto p-8'>
        {productLocalization.affiliate.content.map((item, index) => (
          <>
            <Text bold size={SIZE_ENUM.MD} className='my-2 text-[#454545]'>
              {item.title}
            </Text>
            <Container className='text-sm text-[#76767C]'>
              {formatParagraphNewLine(item.description)}
            </Container>
          </>
        ))}
      </Container>
      <Container className='h-[140px] border-t-[1px] border-t-[#EDEEF2] bg-white p-8 pt-3'>
        <Group className='flex items-center gap-2 py-4'>
          <CheckBox checked={accepted} onChange={setAccepted} className='bg-[#E5E8EB]' />

          <Label className='text-sm text-[#5B5C60]'>
            {productLocalization.affiliate.readAndAcceptRules}
          </Label>
        </Group>
        <Container center className='justify-end'>
          <Button
            size={SIZE_ENUM.MD}
            className='hover:bg-[#EB8800]'
            disabled={!accepted}
            onClick={handleAcceptAffiliateRules}
            isLoading={isLoading}
          >
            {productLocalization.affiliate.acceptAndGetLink}
          </Button>
        </Container>
      </Container>
    </Container>
  );
}
